import React, { useCallback, useState } from "react";

import { SERVICE_STATUS_CLICK } from "../../constants/Events";
import { useTrackEvent } from "../../libs/analytics";
import { useServiceStatus } from "./service-status-context";
import {
  BANNER_DISMISS_STORAGE_KEY,
  STATUS_PAGE_URL,
} from "./status-feed.constants";

// Dismissal lives for the session only, keyed by the incident being shown, so
// a new or escalated incident re-shows a banner the user already dismissed —
// and a fresh session always starts loud.
function readDismissedKey(): string | null {
  try {
    return window.sessionStorage.getItem(BANNER_DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDismissedKey(key: string): void {
  try {
    window.sessionStorage.setItem(BANNER_DISMISS_STORAGE_KEY, key);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Losing the
    // dismissal is a far smaller problem than throwing during an incident.
  }
}

const TONE = {
  outage: {
    wrapper: "bg-red-50 border-red-200",
    icon: "fa-regular fa-circle-exclamation text-red-600",
    title: "text-red-900",
    body: "text-red-800",
    link: "text-red-700 hover:text-red-900",
    dismiss: "text-red-600 hover:text-red-800 hover:bg-red-100",
    heading: "Sked is experiencing an outage",
  },
  degraded: {
    wrapper: "bg-amber-50 border-amber-200",
    icon: "fa-regular fa-triangle-exclamation text-amber-600",
    title: "text-amber-900",
    body: "text-amber-800",
    link: "text-amber-700 hover:text-amber-900",
    dismiss: "text-amber-600 hover:text-amber-800 hover:bg-amber-100",
    heading: "Some Sked services are degraded",
  },
} as const;

/**
 * Full-width incident banner, mirroring `SubscriptionBanner`'s sticky-top
 * shell. Red for an outage, amber for degraded, and nothing at all otherwise —
 * including scheduled maintenance (badge only) and feed errors (fail silent).
 */
export function ServiceStatusBanner(): React.ReactElement | null {
  const view = useServiceStatus();
  const trackEvent = useTrackEvent();
  const [dismissedKey, setDismissedKey] = useState<string | null>(
    readDismissedKey
  );

  const dismiss = useCallback(() => {
    writeDismissedKey(view.bannerKey);
    setDismissedKey(view.bannerKey);
  }, [view.bannerKey]);

  const trackClick = useCallback(() => {
    trackEvent({
      eventName: SERVICE_STATUS_CLICK,
      metadata: { source: "banner", state: view.overall },
      useServices: ["AMP", "HS"],
    });
  }, [trackEvent, view.overall]);

  if (view.bannerState === "none" || dismissedKey === view.bannerKey) {
    return null;
  }

  const tone = TONE[view.bannerState];
  const title = view.incident?.title ?? tone.heading;

  return (
    <div
      className={`sticky top-0 z-50 w-full border-b-2 px-6 py-4 ${tone.wrapper}`}
      role="status"
      aria-live="polite"
      data-testid="service-status-banner"
      data-state={view.bannerState}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <i className={`${tone.icon} text-xl`} />
          <div className="text-sm">
            <span className={`font-semibold font-gotham ${tone.title}`}>
              {title}
            </span>
            <span className={`block mt-1 font-inter ${tone.body}`}>
              We're working on it. Live updates are on our status page.{" "}
              <a
                href={STATUS_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`underline ${tone.link}`}
                onClick={trackClick}
              >
                View status
                <i className="fa-regular fa-arrow-up-right-from-square ml-1 text-xs" />
              </a>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={dismiss}
            className={`p-2 rounded-lg transition-colors ${tone.dismiss}`}
            aria-label="Dismiss service status banner"
          >
            <i className="fa-regular fa-times text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}
